import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Text from '../elements/Typo/Text'
import Icon from './icons'
import { useLanguage } from '../store/api/hooks/langHook'
import { useLanguageSelector } from '../store/selectors/fpLoaderSelector'
import toast from 'react-hot-toast'
import Button from '../elements/button'
import { useUserApi } from '../store/api/hooks/userApiHook'
import { useDispatch } from 'react-redux'
import { ACTIONS } from '../_config'
import { trackEvent } from '../_common/functions'

interface Language {
    code: string
    label: string
}

const languages: Language[] = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Spanish' },
    { code: 'pt', label: 'Portuguese' },
]

const LanguageSelector: React.FC = () => {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const currentLanguage = useLanguageSelector()
    const { setLanguage } = useLanguage()
    const userAPI: any = useUserApi()
    const dispatch = useDispatch()

    const selectedLanguage = languages.find((lang) => lang.code === currentLanguage) || languages[0]

    const toggleDropdown = () => {
        if (!isOpen) {
            trackEvent('landing_language_dropdown_opened', { current_language: currentLanguage })
        }
        setIsOpen(!isOpen)
    }

    const setLanguageDefault = (code: string) => {
        userAPI.setLanguage(
            { language: code },
            (resp: any) => {
                console.log('set')
                dispatch({
                    type: ACTIONS.USER.UPDATE_USER,
                    payload: {
                        user: {
                            language: code,
                        },
                    },
                })
            },
            (err: any) => {
                console.log('failed')
            },
        )
    }

    const showSetDefaultLanguageToast = (language: Language) => {
        toast.custom(
            (toastInstance) => (
                <div className={`bg-white shadow-lg rounded-lg px-4 py-3 flex items-center gap-4 transition-all ${toastInstance.visible ? 'opacity-100' : 'opacity-0'}`}>
                    <p className="text-sm font-medium text-gray-800">{t('Set as default Language?')}</p>

                    <div className="flex items-center gap-2">
                        <Button
                            size="xxs"
                            theme="secondary"
                            onClick={() => {
                                trackEvent('landing_language_set_default_dismissed', { language: language.code })
                                toast.dismiss(toastInstance.id)
                            }}
                        >
                            {t('No')}
                        </Button>
                        <Button
                            size="xxs"
                            onClick={() => {
                                trackEvent('landing_language_set_default', { language: language.code })
                                setLanguageDefault(language.code)
                                toast.dismiss(toastInstance.id)
                            }}
                        >
                            {t('Make it Default')}
                        </Button>
                    </div>
                </div>
            ),
            { duration: 6000 },
        ) // keeps it open until user clicks
    }

    const handleLanguageChange = (language: Language) => {
        trackEvent('landing_language_changed', { from_language: currentLanguage, to_language: language.code })
        setLanguage(language.code)
        setIsOpen(false)
        setTimeout(() => {
            showSetDefaultLanguageToast(language)
        }, 2000)
    }

    const getLanguageLabel = (code: string) => {
        const language = languages.find((lang) => lang.code === code)
        if (currentLanguage == 'en') {
            return language ? language.label : 'Chinese'
        }
        return language ? `${language.label} (${t(language.label)})` : t('Chinese')
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement
            if (isOpen && !target.closest('.dropdown')) {
                setIsOpen(false)
            }
        }

        document.addEventListener('click', handleClickOutside)
        return () => document.removeEventListener('click', handleClickOutside)
    }, [isOpen])

    return (
        <div className="dropdown dropdown-end">
            <button type="button" tabIndex={0} onClick={toggleDropdown} className="flex items-center gap-1 border border-gray-300 rounded-md px-2 py-1 hover:bg-gray-50 transition-colors" aria-label={t('Select Language')}>
                <Text size="sm" className="font-medium">
                    {selectedLanguage.label}
                </Text>
                <Icon name="down" size={0.5} />
            </button>

            {isOpen && (
                <ul className="dropdown-content z-[1] min-w-[12rem] bg-white shadow-md rounded-md px-0 py-1 mt-1">
                    {languages.map((language) => (
                        <li key={language.code} className="my-1">
                            <button
                                type="button"
                                onClick={() => handleLanguageChange(language)}
                                className={`flex items-center gap-2 w-full px-4 py-2 hover:bg-gray-100 transition-colors ${selectedLanguage.code === language.code ? 'bg-gray-50' : ''}`}
                            >
                                <Text size="sm" className="font-medium flex-1 text-left">
                                    {getLanguageLabel(language.code)}
                                </Text>
                                {selectedLanguage.code === language.code && <Icon name="check" size={0.583} className="text-primary" />}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

export default LanguageSelector
